import { readFile, stat, writeFile } from "node:fs/promises";
import path from "node:path";

import { buildImportGraph } from "../lib/import-graph.js";
import { ensureDir } from "../lib/fs.js";
import { buildUnifiedDiff } from "../lib/text-diff.js";
import {
  createCopyId,
  ensureSafeTargetPath,
  fileExists,
  getDependencyVersion,
  getOrCreateDependencySection,
  isBinaryBuffer,
  parseManifest,
  readJsonFile,
  resolveSingleExportDirectory,
  resolveManifestCandidateFiles,
  sortDependencySections,
  toProjectRelative,
  toPosixPath,
  type PackageJsonRecord,
} from "./copy-service-shared.js";
import type {
  PrimeUiConflictFile,
  PrimeUiCopyComponentConflictReport,
  PrimeUiCopyComponentGuidance,
  PrimeUiCopyComponentResult,
  PrimeUiCopyComponentSelectedBlock,
  PrimeUiCopyConflictReportEntry,
  PrimeUiDependencyToAdd,
  PrimeUiDependencyVersionConflict,
  PrimeUiProjectPageDetailsResult,
} from "../types.js";

type CopyComponentServiceInput = {
  projectRoot: string;
  exportsRoot: string;
  originPageSlug: string;
  blockId: string;
  componentId: string;
  pageDetails: PrimeUiProjectPageDetailsResult;
};

function normalizeSlug(slug: string): string {
  const trimmed = slug.trim();
  if (!trimmed || trimmed === "/") {
    return "/";
  }

  const withLeadingSlash = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return withLeadingSlash.replace(/\/+$/, "") || "/";
}

function normalizeRequiredValue(value: string, label: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error(`${label} must not be empty.`);
  }

  return trimmed;
}

function resolveTargetFilePath(input: {
  sourceFilePath: string;
  sourcePagePath: string;
  sourceComponentsPath: string;
  targetPagePath: string;
  targetComponentsPath: string;
  exportRoot: string;
  projectRoot: string;
}): string {
  const {
    sourceFilePath,
    sourcePagePath,
    sourceComponentsPath,
    targetPagePath,
    targetComponentsPath,
    exportRoot,
    projectRoot,
  } = input;

  if (sourceFilePath === sourcePagePath) {
    return targetPagePath;
  }

  const componentsPrefix = `${sourceComponentsPath}${path.sep}`;
  if (
    sourceFilePath === sourceComponentsPath ||
    sourceFilePath.startsWith(componentsPrefix)
  ) {
    const relativeToComponents = path.relative(
      sourceComponentsPath,
      sourceFilePath,
    );
    return path.join(targetComponentsPath, relativeToComponents);
  }

  const relativeToExport = path.relative(exportRoot, sourceFilePath);
  if (relativeToExport.startsWith("..") || path.isAbsolute(relativeToExport)) {
    throw new Error(`Source file is outside export root: ${sourceFilePath}`);
  }

  return path.join(projectRoot, relativeToExport);
}

function buildContentKey(componentId: string, occurrenceIndex: number): string {
  return occurrenceIndex === 1
    ? componentId
    : `${componentId}-${occurrenceIndex}`;
}

function resolveSelectedBlock(input: {
  pageDetails: PrimeUiProjectPageDetailsResult;
  blockId: string;
  componentId: string;
}): PrimeUiCopyComponentSelectedBlock {
  const { components } = input.pageDetails;
  if (components === null) {
    throw new Error(
      `Components are unavailable for page "${input.pageDetails.page.slug}" because no active variant is set.`,
    );
  }

  let occurrenceIndex = 0;
  for (const component of components) {
    if (component.componentId !== input.componentId) {
      continue;
    }

    occurrenceIndex += 1;
    if (component.blockId !== input.blockId) {
      continue;
    }

    return {
      blockId: component.blockId,
      componentId: component.componentId,
      slot: component.slot,
      props: component.props,
      occurrenceIndex,
      contentKey: buildContentKey(component.componentId, occurrenceIndex),
    };
  }

  throw new Error(
    `Block not found for page "${input.pageDetails.page.slug}" using blockId "${input.blockId}" and componentId "${input.componentId}". Run inspect_page and confirm the selected block instance.`,
  );
}

async function createConflictReportEntry(input: {
  sourceFilePath: string;
  targetFilePath: string;
  exportPath: string;
  projectRoot: string;
}): Promise<PrimeUiCopyConflictReportEntry | null> {
  const sourceBuffer = await readFile(input.sourceFilePath);
  let targetBuffer: Buffer | null = null;
  try {
    targetBuffer = await readFile(input.targetFilePath);
  } catch {
    targetBuffer = null;
  }

  if (!targetBuffer || sourceBuffer.equals(targetBuffer)) {
    return null;
  }

  const sourceRelative = toProjectRelative(
    input.exportPath,
    input.sourceFilePath,
  );
  const targetRelative = toProjectRelative(
    input.projectRoot,
    input.targetFilePath,
  );
  const isBinary = isBinaryBuffer(sourceBuffer) || isBinaryBuffer(targetBuffer);

  return {
    sourcePath: sourceRelative,
    targetPath: targetRelative,
    diff: isBinary
      ? "Binary files differ."
      : buildUnifiedDiff({
          oldText: targetBuffer.toString("utf-8"),
          newText: sourceBuffer.toString("utf-8"),
          oldLabel: `user/${targetRelative}`,
          newLabel: `export/${sourceRelative}`,
        }),
    isBinary,
  };
}

async function applyDependencyRequirements(input: {
  exportPath: string;
  projectRoot: string;
  requiredPackageNames: Iterable<string>;
}): Promise<{
  addedDependencies: PrimeUiDependencyToAdd[];
  dependenciesVersionConflicts: PrimeUiDependencyVersionConflict[];
}> {
  const exportPackageJsonPath = path.join(input.exportPath, "package.json");
  const userPackageJsonPath = path.join(input.projectRoot, "package.json");

  if (!(await fileExists(exportPackageJsonPath))) {
    throw new Error(`Export package.json not found: ${exportPackageJsonPath}`);
  }

  if (!(await fileExists(userPackageJsonPath))) {
    throw new Error(`User package.json not found: ${userPackageJsonPath}`);
  }

  const exportPackageJson = await readJsonFile<PackageJsonRecord>(
    exportPackageJsonPath,
  );
  const userPackageJson =
    await readJsonFile<PackageJsonRecord>(userPackageJsonPath);

  const addedDependencies: PrimeUiDependencyToAdd[] = [];
  const dependenciesVersionConflicts: PrimeUiDependencyVersionConflict[] = [];

  for (const packageName of new Set(input.requiredPackageNames)) {
    const exportDependency = getDependencyVersion(
      exportPackageJson,
      packageName,
    );
    if (!exportDependency) {
      continue;
    }

    const userDependency = getDependencyVersion(userPackageJson, packageName);
    if (!userDependency) {
      const sectionData = getOrCreateDependencySection(
        userPackageJson,
        exportDependency.section,
      );
      sectionData[packageName] = exportDependency.version;
      addedDependencies.push({
        packageName,
        version: exportDependency.version,
        section: exportDependency.section,
      });
      continue;
    }

    if (userDependency.version !== exportDependency.version) {
      dependenciesVersionConflicts.push({
        packageName,
        section: exportDependency.section,
        exportVersion: exportDependency.version,
        userVersion: userDependency.version,
      });
    }
  }

  if (addedDependencies.length > 0) {
    sortDependencySections(userPackageJson);
    await writeFile(
      userPackageJsonPath,
      `${JSON.stringify(userPackageJson, null, 2)}\n`,
      "utf-8",
    );
  }

  return {
    addedDependencies: addedDependencies.sort((a, b) =>
      a.packageName.localeCompare(b.packageName),
    ),
    dependenciesVersionConflicts: dependenciesVersionConflicts.sort((a, b) =>
      a.packageName.localeCompare(b.packageName),
    ),
  };
}

function buildGuidance(input: {
  selectedBlock: PrimeUiCopyComponentSelectedBlock;
  manualInsertionTargets: string[];
  potentialDependencyFiles: string[];
  potentialConflictFiles: string[];
  primaryComponentResolved: boolean;
  addedDependencies: PrimeUiDependencyToAdd[];
  dependenciesVersionConflicts: PrimeUiDependencyVersionConflict[];
  missingManifestFiles: string[];
}): PrimeUiCopyComponentGuidance {
  const requiredActions = [
    `Use selectedBlock.props as the exact prop payload for block "${input.selectedBlock.blockId}" and contentKey "${input.selectedBlock.contentKey}".`,
    `Review manualInsertionTargets and wire the selected component instance into: ${input.manualInsertionTargets.join(", ")}.`,
    input.potentialDependencyFiles.length > 0
      ? "Review potentialDependencyFiles first if the imported component is incomplete, because they are the most likely supporting files."
      : "If the imported component is incomplete, compare the page file against the downloaded export before touching unrelated files.",
    input.primaryComponentResolved
      ? "If behavior is incorrect, compare the local files against the downloaded reference export using exportPath."
      : "Resolve the canonical component file manually from the downloaded reference export before continuing integration.",
  ];

  if (input.addedDependencies.length > 0) {
    requiredActions.push(
      "Install the newly added dependencies before running the copied component locally.",
    );
  }

  if (input.dependenciesVersionConflicts.length > 0) {
    requiredActions.push(
      "Resolve the reported dependency version conflicts before treating the transfer as complete.",
    );
  }

  if (input.missingManifestFiles.length > 0) {
    requiredActions.push(
      "Restore the missing export files referenced by the manifest before treating the transfer as complete.",
    );
  }

  const notes = [
    "copy_component copies only the canonical component file when it can resolve that file reliably.",
    "potentialDependencyFiles and potentialConflictFiles are hints, not mandatory full-page merge instructions.",
  ];

  if (input.potentialConflictFiles.length > 0) {
    notes.push(
      "potentialConflictFiles mark related files that already differ locally and may matter only if behavior diverges after integration.",
    );
  }

  return {
    requiredActions,
    notes,
  };
}

export async function copyComponentFromExport(
  input: CopyComponentServiceInput,
): Promise<PrimeUiCopyComponentResult> {
  const normalizedOriginSlug = normalizeSlug(input.originPageSlug);
  const normalizedBlockId = normalizeRequiredValue(input.blockId, "blockId");
  const normalizedComponentId = normalizeRequiredValue(
    input.componentId,
    "componentId",
  );

  const { exportId, exportPath } = await resolveSingleExportDirectory(
    input.exportsRoot,
  );
  const manifestPath = path.join(
    input.exportsRoot,
    `${exportId}.manifest.json`,
  );

  if (!(await fileExists(manifestPath))) {
    throw new Error(
      `Export manifest is missing at ${manifestPath}. Run create_export and download_export to regenerate it.`,
    );
  }

  const manifest = parseManifest(await readJsonFile<unknown>(manifestPath));
  if (manifest.export.id !== exportId) {
    throw new Error(
      `Manifest export id mismatch: expected ${exportId}, got ${manifest.export.id}. Re-run create_export and download_export.`,
    );
  }

  const page = manifest.pages.find(
    (item) => normalizeSlug(item.slug) === normalizedOriginSlug,
  );
  if (!page) {
    throw new Error(
      `Page not found in manifest for slug: ${normalizedOriginSlug}`,
    );
  }

  const candidateFiles = await resolveManifestCandidateFiles({
    exportPath,
    manifestOwnerLabel: `page "${normalizedOriginSlug}"`,
    files: page.manifest.files,
    requireExisting: false,
  });

  const selectedBlock = resolveSelectedBlock({
    pageDetails: input.pageDetails,
    blockId: normalizedBlockId,
    componentId: normalizedComponentId,
  });

  const sourcePagePath = path.join(exportPath, page.pagePath);
  const sourceComponentsPath = path.join(exportPath, page.componentsPath);
  const targetPagePath = path.join(input.projectRoot, page.pagePath);
  const targetComponentsPath = path.join(
    input.projectRoot,
    page.componentsPath,
  );

  ensureSafeTargetPath(input.projectRoot, targetPagePath);
  ensureSafeTargetPath(input.projectRoot, targetComponentsPath);

  const manualInsertionTargets = [
    toProjectRelative(input.projectRoot, targetPagePath),
  ];

  const primarySourceCandidates = [
    toPosixPath(path.join(page.componentsPath, `${normalizedComponentId}.tsx`)),
    toPosixPath(
      path.join(page.componentsPath, normalizedComponentId, "index.tsx"),
    ),
  ];
  const primaryCandidateTargetPaths = new Set<string>();
  let primaryComponentFile: string | null = null;
  let resolvedPrimarySourcePath: string | null = null;

  for (const sourceRelativePath of primarySourceCandidates) {
    const sourceCandidatePath = path.join(exportPath, sourceRelativePath);
    const targetCandidatePath = resolveTargetFilePath({
      sourceFilePath: sourceCandidatePath,
      sourcePagePath,
      sourceComponentsPath,
      targetPagePath,
      targetComponentsPath,
      exportRoot: exportPath,
      projectRoot: input.projectRoot,
    });
    ensureSafeTargetPath(input.projectRoot, targetCandidatePath);
    primaryCandidateTargetPaths.add(
      toProjectRelative(input.projectRoot, targetCandidatePath),
    );

    const stats = await stat(sourceCandidatePath).catch(() => null);
    if (!stats?.isFile()) {
      continue;
    }

    if (!resolvedPrimarySourcePath) {
      resolvedPrimarySourcePath = sourceCandidatePath;
      primaryComponentFile = toProjectRelative(
        input.projectRoot,
        targetCandidatePath,
      );
    }
  }

  const newFiles: string[] = [];
  const identicalFiles: string[] = [];
  const conflictFiles: PrimeUiConflictFile[] = [];
  const actualConflicts: PrimeUiCopyConflictReportEntry[] = [];
  const missingManifestFiles = new Set<string>();

  if (resolvedPrimarySourcePath && primaryComponentFile) {
    const targetPrimaryPath = path.join(
      input.projectRoot,
      primaryComponentFile,
    );
    ensureSafeTargetPath(input.projectRoot, targetPrimaryPath);

    const sourceBuffer = await readFile(resolvedPrimarySourcePath);
    let targetBuffer: Buffer | null = null;
    try {
      targetBuffer = await readFile(targetPrimaryPath);
    } catch {
      targetBuffer = null;
    }

    if (!targetBuffer) {
      await ensureDir(path.dirname(targetPrimaryPath));
      await writeFile(targetPrimaryPath, sourceBuffer);
      newFiles.push(primaryComponentFile);
    } else if (sourceBuffer.equals(targetBuffer)) {
      identicalFiles.push(primaryComponentFile);
    } else {
      const entry = await createConflictReportEntry({
        sourceFilePath: resolvedPrimarySourcePath,
        targetFilePath: targetPrimaryPath,
        exportPath,
        projectRoot: input.projectRoot,
      });
      if (entry) {
        actualConflicts.push(entry);
      }
    }
  }

  const dependencyGraph = resolvedPrimarySourcePath
    ? await buildImportGraph({
        projectRoot: exportPath,
        entryFiles: [resolvedPrimarySourcePath],
      })
    : { externalPackages: [] };

  const { addedDependencies, dependenciesVersionConflicts } =
    resolvedPrimarySourcePath
      ? await applyDependencyRequirements({
          exportPath,
          projectRoot: input.projectRoot,
          requiredPackageNames: dependencyGraph.externalPackages,
        })
      : {
          addedDependencies: [] as PrimeUiDependencyToAdd[],
          dependenciesVersionConflicts:
            [] as PrimeUiDependencyVersionConflict[],
        };

  const potentialDependencyTargets = new Set<string>();
  const potentialConflictEntries = new Map<
    string,
    PrimeUiCopyConflictReportEntry
  >();

  for (const sourceFilePath of candidateFiles) {
    const targetFilePath = resolveTargetFilePath({
      sourceFilePath,
      sourcePagePath,
      sourceComponentsPath,
      targetPagePath,
      targetComponentsPath,
      exportRoot: exportPath,
      projectRoot: input.projectRoot,
    });
    ensureSafeTargetPath(input.projectRoot, targetFilePath);

    const targetRelative = toProjectRelative(input.projectRoot, targetFilePath);
    const sourceRelative = toProjectRelative(exportPath, sourceFilePath);
    const sourceExists = await fileExists(sourceFilePath);
    if (!sourceExists) {
      missingManifestFiles.add(sourceRelative);
      continue;
    }

    if (
      manualInsertionTargets.includes(targetRelative) ||
      primaryCandidateTargetPaths.has(targetRelative)
    ) {
      continue;
    }

    potentialDependencyTargets.add(targetRelative);

    const targetExists = await fileExists(targetFilePath);
    if (!targetExists) {
      continue;
    }

    const entry = await createConflictReportEntry({
      sourceFilePath,
      targetFilePath,
      exportPath,
      projectRoot: input.projectRoot,
    });
    if (entry) {
      potentialConflictEntries.set(targetRelative, entry);
    }
  }

  const sortedActualConflicts = actualConflicts.sort((a, b) =>
    a.targetPath.localeCompare(b.targetPath),
  );
  for (const entry of sortedActualConflicts) {
    conflictFiles.push({
      targetPath: entry.targetPath,
      isBinary: entry.isBinary,
    });
  }

  const sortedPotentialConflictEntries = [
    ...potentialConflictEntries.values(),
  ].sort((a, b) => a.targetPath.localeCompare(b.targetPath));
  const potentialDependencyFiles = [...potentialDependencyTargets].sort(
    (a, b) => a.localeCompare(b),
  );
  const potentialConflictFiles = sortedPotentialConflictEntries.map(
    (entry) => entry.targetPath,
  );

  const copyId = createCopyId();
  const reportPath = path.join(
    input.exportsRoot,
    `${exportId}.copy-component-report-${copyId}.json`,
  );
  const report: PrimeUiCopyComponentConflictReport = {
    reportPath,
    generatedAt: new Date().toISOString(),
    exportId,
    originPageSlug: normalizedOriginSlug,
    blockId: normalizedBlockId,
    componentId: normalizedComponentId,
    primaryComponentCandidates: primarySourceCandidates,
    primaryComponentFile,
    manualInsertionTargets,
    potentialDependencyFiles,
    actualConflicts: sortedActualConflicts,
    potentialConflicts: sortedPotentialConflictEntries,
    selectedBlock,
  };
  await writeFile(reportPath, `${JSON.stringify(report, null, 2)}\n`, "utf-8");

  const needsReview =
    !primaryComponentFile ||
    conflictFiles.length > 0 ||
    dependenciesVersionConflicts.length > 0 ||
    missingManifestFiles.size > 0;
  const missingManifestFileList = [...missingManifestFiles].sort((a, b) =>
    a.localeCompare(b),
  );
  const guidance = buildGuidance({
    selectedBlock,
    manualInsertionTargets,
    potentialDependencyFiles,
    potentialConflictFiles,
    primaryComponentResolved: Boolean(primaryComponentFile),
    addedDependencies,
    dependenciesVersionConflicts,
    missingManifestFiles: missingManifestFileList,
  });

  const messageParts: string[] = [];
  if (!primaryComponentFile) {
    messageParts.push(
      `Primary component file could not be resolved for block "${normalizedBlockId}" (${normalizedComponentId}). Review ${reportPath}, inspect the downloaded export at ${exportPath}, and resolve the component file manually before continuing.`,
    );
  } else if (conflictFiles.length > 0) {
    messageParts.push(
      `Primary component file conflicts with an existing local file. Review ${reportPath} for the exact diff before completing block-level transfer.`,
    );
  } else if (newFiles.length > 0) {
    messageParts.push(
      `Primary component file copied successfully with no direct conflicts. Review ${reportPath} for local details if needed.`,
    );
  } else {
    messageParts.push(
      `Primary component file already exists and is byte-identical. Review ${reportPath} for local details if needed.`,
    );
  }

  if (missingManifestFileList.length > 0) {
    messageParts.push(
      `Referenced export files are missing: ${missingManifestFileList.join(", ")}. Review ${reportPath} and restore the missing files before treating this transfer as complete.`,
    );
  }

  if (addedDependencies.length > 0) {
    messageParts.push(
      "Missing dependencies were added to package.json but are NOT installed yet. Run dependency installation manually with the package manager used by the target project.",
    );
  }

  if (dependenciesVersionConflicts.length > 0) {
    messageParts.push(
      `Dependency version conflicts detected. Manual resolution is required before continuing block-level integration. Review ${reportPath} for full local details.`,
    );
  }

  messageParts.push(
    `Use selectedBlock.props with contentKey "${selectedBlock.contentKey}" for the chosen block instance.`,
  );
  messageParts.push(
    `manualInsertionTargets identifies where this component usually needs to be wired: ${manualInsertionTargets.join(", ")}.`,
  );

  if (potentialDependencyFiles.length > 0) {
    messageParts.push(
      "potentialDependencyFiles are prioritized hints, not mandatory full-page merge instructions. Review them first if the imported component behavior is incomplete.",
    );
  }

  if (potentialConflictFiles.length > 0) {
    messageParts.push(
      "potentialConflictFiles already differ locally. Compare them against the downloaded export only if the imported component behavior diverges.",
    );
  }

  return {
    status: needsReview ? "needs_review" : "completed",
    message: messageParts.join(" "),
    reportPath,
    exportId,
    exportPath,
    manifestPath,
    originPageSlug: normalizedOriginSlug,
    blockId: normalizedBlockId,
    componentId: normalizedComponentId,
    selectedBlock,
    primaryComponentFile,
    manualInsertionTargets,
    newFiles,
    identicalFiles,
    conflictFiles,
    potentialDependencyFiles,
    potentialConflictFiles,
    addedDependencies,
    dependenciesVersionConflicts,
    guidance,
  };
}
